import { Link } from "react-router-dom";
import { Moon, Sun, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useTheme } from "@/context/ThemeProvider";
import {
    BRAND, BRAND_SOLID, BRAND_HOVER, BRAND_RED, BRAND_RED_SOLID, BRAND_55, paletteClasses,
} from "@/lib/landing-theme";

/**
 * Marketing-page header shared across Landing, About, Contact.
 * Renders a desktop nav + a mobile hamburger drawer when `showNav` is on.
 * Testids stay unique per page via the `testidPrefix` prop.
 */
const NAV_ITEMS = [
    { label: "Home",     kind: "route",  to: "/"    },
    { label: "About",    kind: "route",  to: "/about" },
    { label: "Features", kind: "anchor", to: "/#features" },
    { label: "Pricing",  kind: "anchor", to: "/#pricing" },
    { label: "FAQ",      kind: "route",  to: "/faq" },
    { label: "Contact",  kind: "route",  to: "/contact" },
];

export default function MarketingHeader({
    testidPrefix = "marketing",
    showNav      = false,
    showLogin    = false,
}) {
    const { theme, toggle } = useTheme();
    const isDark = theme === "dark";
    const p = paletteClasses(isDark);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Lock body scroll while drawer is open + close on escape
    useEffect(() => {
        if (!drawerOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && setDrawerOpen(false);
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [drawerOpen]);

    const renderNavItem = (item, testid, onClick, className = "") => item.kind === "route" ? (
        <Link key={item.label} to={item.to} onClick={onClick}
              className={`hover:text-[#006600] transition-colors ${className}`}
              data-testid={testid}>{item.label}</Link>
    ) : (
        <a key={item.label} href={item.to} onClick={onClick}
           className={`hover:text-[#006600] transition-colors ${className}`}
           data-testid={testid}>{item.label}</a>
    );

    return (
        <>
            <header
                className={`sticky top-0 z-50 backdrop-blur-md border-b ${p.headerBg} px-4 md:px-8 py-3 flex items-center justify-between`}
                data-testid={`${testidPrefix}-header`}
            >
                <Link to="/" className="flex items-center gap-2" data-testid={`${testidPrefix}-logo`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white tracking-tight text-sm"
                         style={{ background: BRAND_RED_SOLID }}>MD</div>
                    <span className="font-bold text-lg md:text-xl tracking-tight" style={{ color: BRAND_RED }}>
                        MELIAN DIALOGUE
                    </span>
                </Link>

                <div className="flex items-center gap-2 md:gap-4">
                    {showNav && (
                        <nav className="hidden md:flex items-center gap-6 text-sm font-semibold"
                             data-testid={`${testidPrefix}-nav`}>
                            {NAV_ITEMS.map((it) => renderNavItem(it, `nav-${it.label.toLowerCase()}`))}
                        </nav>
                    )}

                    <button onClick={toggle}
                        className={`p-2 rounded-full border transition-colors ${p.outlineIcon}`}
                        aria-label="Toggle theme" data-testid={`${testidPrefix}-theme-toggle`}>
                        {isDark ? <Sun size={18} /> : <Moon size={18} />}
                    </button>

                    {showLogin && (
                        <Link to="/login"
                              className="hidden sm:inline text-sm font-medium hover:text-[#006600] transition-colors"
                              data-testid={`${testidPrefix}-login-btn`}>
                            Login
                        </Link>
                    )}

                    <Link to="/register?intent=obondo_trial"
                        className="hidden sm:inline-flex min-h-[44px] items-center text-white px-4 py-2 rounded-lg text-sm font-extrabold shadow-lg transition-all transform active:scale-95 hover:-translate-y-0.5"
                        style={{ background: BRAND_SOLID, boxShadow: `0 8px 24px -8px ${BRAND_55}` }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = BRAND_HOVER)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = BRAND)}
                        data-testid={`${testidPrefix}-signup-btn`}>
                        Create Free Account
                    </Link>

                    {showNav && (
                        <button
                            onClick={() => setDrawerOpen(true)}
                            aria-label="Open menu"
                            className={`md:hidden p-2 rounded-lg border transition-colors ${p.outlineIcon}`}
                            data-testid={`${testidPrefix}-hamburger`}>
                            <Menu size={20} />
                        </button>
                    )}
                </div>
            </header>

            {/* Mobile drawer */}
            {showNav && (
                <>
                    <div
                        className={`fixed inset-0 z-[99] bg-black/50 transition-opacity md:hidden ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
                        onClick={() => setDrawerOpen(false)}
                        data-testid={`${testidPrefix}-drawer-overlay`}
                    />
                    <aside
                        className={`fixed top-0 right-0 z-[100] h-screen w-[280px] transform transition-transform duration-300 md:hidden ${p.shell} border-l ${isDark ? "border-zinc-800" : "border-slate-200"} ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
                        role="dialog"
                        aria-label="Mobile navigation"
                        data-testid={`${testidPrefix}-drawer`}
                    >
                        <div className="flex items-center justify-between p-5 border-b border-zinc-800/20">
                            <span className="font-bold text-base tracking-tight" style={{ color: BRAND_RED }}>MENU</span>
                            <button
                                onClick={() => setDrawerOpen(false)}
                                aria-label="Close menu"
                                className={`p-2 rounded-lg border transition-colors ${p.outlineIcon}`}
                                data-testid={`${testidPrefix}-drawer-close`}>
                                <X size={18} />
                            </button>
                        </div>
                        <nav className="flex flex-col gap-1 p-4" data-testid={`${testidPrefix}-drawer-nav`}>
                            {NAV_ITEMS.map((it) => renderNavItem(
                                it,
                                `drawer-nav-${it.label.toLowerCase()}`,
                                () => setDrawerOpen(false),
                                `block rounded-lg px-3 py-3 text-base font-semibold ${isDark ? "hover:bg-zinc-900" : "hover:bg-slate-100"}`
                            ))}
                        </nav>
                        <div className="mt-2 p-4 flex flex-col gap-3">
                            {showLogin && (
                                <Link to="/login"
                                      onClick={() => setDrawerOpen(false)}
                                      className={`text-center rounded-lg border py-3 text-sm font-semibold ${p.outlineBtn}`}
                                      data-testid={`${testidPrefix}-drawer-login`}>
                                    Login
                                </Link>
                            )}
                            <Link to="/register?intent=obondo_trial"
                                  onClick={() => setDrawerOpen(false)}
                                  className="text-center min-h-[44px] flex items-center justify-center text-white py-3 rounded-lg text-sm font-extrabold shadow-lg"
                                  style={{ background: BRAND_SOLID, boxShadow: `0 8px 24px -8px ${BRAND_55}` }}
                                  data-testid={`${testidPrefix}-drawer-signup`}>
                                Create Free Account
                            </Link>
                        </div>
                    </aside>
                </>
            )}
        </>
    );
}
